import { Button, Card, Col, Divider, Flex, List, Row, Typography } from "antd";
import { ArrowRightOutlined, CalendarOutlined, UserOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs"
import { useNavigate } from "react-router-dom";
import { setIsLoading, setNewsList } from "../slices/newsSlice";
import { getAnnouncementNewsPaging } from "../services/newsApi";
import { generateRandomString } from "utils/helper";
import { Alert } from 'components/elements';
import DOMPurify from "dompurify";
import "../styles/Html.css"

const { Paragraph } = Typography;

function useAllNews() {

    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { newsList, isLoading } = useSelector((state) => state.news);
    const { currentCompany, currentEmployee } = useSelector((state) => state.company);
    const [pagination, setPagination] = useState({ page: 0, size: 10, pageSize: 10 });
    const [total, setTotal] = useState(0);
    const { roles } = useSelector((state) => state.company);
    const navigate = useNavigate();

    function createImageUrl(base64Data, contentType) {
        return `data:${contentType};base64,${base64Data}`;
    }

    const getNewsList = useCallback(
        async (params = {}) => {
            try {
                dispatch(setIsLoading(true));
                const response = await getAnnouncementNewsPaging({
                    page: pagination.page,
                    size: pagination.size,
                    ...params,
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    employee: currentEmployee,
                });
                dispatch(setNewsList(response.data?.content || []));
                setTotal(response.data?.totalItems)
            } catch (error) {
                Alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, currentEmployee, dispatch, pagination.page, pagination.size]
    );

    useEffect(() => {
        if (roles.includes("ACCESS_NEWS_&_POLICY|NEWS")) {
            getNewsList({})
        }
    }, [currentCompany, getNewsList, pagination, roles])

    const dataSource = useMemo(
        () => {
            if (newsList) {
                const data = newsList?.map((data, i) => {
                    return (
                        {
                            id: data?.announcementNewsId,
                            title: data?.subject,
                            avatar: createImageUrl(data?.cover, "image/png"),
                            description:
                                data?.description,
                            startAnnounce: data?.startAnnounce,
                            announcementBy: data?.announcementBy["name" + (i18n.language == "en" ? "En" : "")],
                        })
                }
                );
                return data;
            } else {
                return []
            }
        }
        , [newsList, i18n.language])


    const allNewsList = useCallback(() => {

        return (
            <List
                loading={isLoading}
                dataSource={dataSource}
                pagination={{
                    current: pagination.page + 1,
                    pageSize: pagination.pageSize,
                    total: total,
                    onChange: (page, pageSize) => {
                        setPagination((prev) => ({ ...prev, page: page - 1, pageSize }));
                    },
                    showTotal: (total, range) =>
                        t("paginate.description")
                            .replace("{min}", range[0])
                            .replace("{max}", range[1])
                            .replace("{total}", total),
                }}
                renderItem={(item) => (
                    <List.Item
                        size="small"
                        key={item.title}
                        style={{ overflowWrap: "break-word", borderBottom: "solid", borderBottomColor: "#cccccc", borderBottomWidth: "1px" }}
                    >
                        <Col span={7}>
                            <img style={{ minWidth: "20vw", maxWidth: "20vw", borderRadius: "16px" }} alt="logo" src={item.avatar}></img>
                        </Col>
                        <Col span={17}>
                            <Flex vertical style={{ rowGap: "2vh" }}>
                                <Flex vertical style={{ rowGap: "1vh", justifyContent: "start" }}>
                                    <Row>
                                        <h3 style={{ color: "#2BA4DD" }}>{item.title}</h3>
                                    </Row>
                                    <Row>
                                        <Paragraph
                                            ellipsis={{
                                                rows: 2,
                                                expandable: false,
                                                symbol: 'more',
                                            }}
                                        >
                                            <div dangerouslySetInnerHTML={{ __html: item.description }} />
                                        </Paragraph>
                                    </Row>
                                </Flex>

                                <Row>
                                    <Col span={12} style={{ color: "#9D9D9D" }}>
                                        <Row style={{ gap: "8px", alignContent: "center" }}>
                                            <CalendarOutlined />
                                            <span>{dayjs(item?.startAnnounce).format(i18n.language === "en" ? "DD MMMM YYYY" : "DD MMMM BBBB")}</span>
                                        </Row>
                                        <Row style={{ gap: "8px", alignContent: "center" }}>
                                            <UserOutlined />
                                            <span>{item.announcementBy}</span>
                                        </Row>
                                    </Col>
                                    <Col span={12} style={{ alignContent: "center", textAlign: "right" }}>
                                        <Button type="text" style={{ color: "#2BA4DD", borderRadius: "20rem", height: "40px", width: "120px" }}
                                            onClick={() => {
                                                console.log("log item", item);
                                                navigate("/news_list/view", { state: item })
                                            }}>
                                            {t("btn.read_more")}
                                            <ArrowRightOutlined />
                                        </Button>
                                    </Col>
                                </Row>
                            </Flex>
                        </Col>
                    </List.Item>
                )}
            >
            </List>
        )
    }, [dataSource, isLoading, i18n.language, navigate, pagination, t])

    function ArticleContent({ html }) {
        const clean = DOMPurify.sanitize(html);

        return (
            <div
                className="article-content"
                dangerouslySetInnerHTML={{ __html: clean }}
            />
        );
    }
    function viewNews(item) {
        return (
            <Flex vertical style={{ rowGap: "24px" }}>
                <Flex vertical>
                    <h2 style={{ color: "#2BA4DD" }}>{item?.title}</h2>

                    <Row style={{ gap: "8px", color: "#9D9D9D" }}>
                        <Flex style={{ gap: "4px" }}>
                            <CalendarOutlined />
                            <span>
                                {dayjs(item?.startAnnounce).format(
                                    i18n.language === "en"
                                        ? "DD MMMM YYYY"
                                        : "DD MMMM BBBB"
                                )}
                            </span>
                        </Flex>

                        <span>|</span>

                        <Flex style={{ gap: "4px" }}>
                            <UserOutlined />
                            <span>{item.announcementBy}</span>
                        </Flex>
                    </Row>
                </Flex>

                <Row style={{ justifyContent: "center" }}>
                    <img
                        style={{
                            minWidth: "70%",
                            maxWidth: "70%",
                            borderRadius: "16px"
                        }}
                        alt="logo"
                        src={item.avatar}
                    />
                </Row>

                <ArticleContent html={item.description} />
            </Flex>
        );
    }

    function moreNews() {
        return (
            <Flex vertical>
                <h3>{t("news.label.more")}</h3>

                <List
                    dataSource={dataSource.slice(0, 3)}
                    renderItem={(item) => (
                        <List.Item
                            size="small"
                            key={item.title}
                            style={{ overflowWrap: "break-word", justifyContent: "center", borderBottom: "0px" }}
                        >
                            <Card
                                hoverable
                                style={{ borderRadius: "16px", border: "solid", borderWidth: "1px", borderColor: "#f0f0f0", width: "15vw" }}
                                styles={{
                                    body: {
                                        padding: "12px"
                                    }
                                }}
                                cover={<img style={{ borderRadius: "16px 16px 0px 0px" }} alt="logo" src={item.avatar}></img>}
                            >
                                <Flex vertical style={{ rowGap: "8px" }}>
                                    <Row>
                                        <h4>{item.title}</h4>
                                    </Row>
                                    <Row justify={"end"}>
                                        <Button type="text" style={{ color: "#2BA4DD", borderRadius: "20rem", height: "40px", width: "120px" }}
                                            onClick={() => {
                                                console.log("log item", item);
                                                navigate("/news_list/view", { state: item })
                                            }}>
                                            {t("btn.read_more")}
                                            <ArrowRightOutlined />
                                        </Button>
                                    </Row>
                                </Flex>
                            </Card>
                        </List.Item>
                    )}
                >
                </List>
                <Row justify={"center"}>
                    <Button size="small" type="text" onClick={() => {
                        navigate("/news_list")
                    }}>
                        <span style={{ color: "#009DE6" }}>{t('home.btn.view-all')}</span></Button>
                </Row>
            </Flex>

        )
    }

    return {
        allNewsList,
        viewNews,
        moreNews
    }
}

export default useAllNews;